import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HANDOVER_RIGHTS_LABEL, HANDOVER_STATUS_LABEL, localInputToUtc, utcToLocalInput } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { dirClass, dirLabel, fmtCountdown, fmtTime, useNow } from '../lib/format';
import type { TradeView } from '../lib/types';
import type { HandoverCandidate, HandoverView, SetupView } from '../lib/types-setups';
import { Button, Card, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented, Select, Textarea } from '../components/ui';

const STATUS_TONE: Record<HandoverView['status'], 'warn' | 'accent' | 'up' | 'neutral' | 'down' | 'live'> = {
  REQUESTED: 'warn',
  ACCEPTED: 'accent',
  ACTIVE: 'live',
  ENDED: 'neutral',
  DECLINED: 'down',
  NOT_ACCEPTED: 'down',
  CANCELLED: 'neutral',
};

export function periodText(h: Pick<HandoverView, 'recurringQuietHours' | 'ownerQuietHours' | 'startsAt' | 'endsAt' | 'owner'>, tz: string) {
  if (h.recurringQuietHours) return `During ${h.owner.name}'s quiet hours${h.ownerQuietHours ? ` (${h.ownerQuietHours.start}–${h.ownerQuietHours.end} ${h.ownerQuietHours.tz})` : ''}, every night until ${fmtTime(h.endsAt, tz, true)}`;
  return `${fmtTime(h.startsAt, tz, true)} → ${fmtTime(h.endsAt, tz, true)}`;
}

function HandoverCard({ h, me, tz, now, onDone }: { h: HandoverView; me: string; tz: string; now: number; onDone: () => void }) {
  const act = useMutation({ mutationFn: (what: 'accept' | 'decline' | 'end') => api(`/handovers/${h.id}/${what}`, { method: 'POST' }), onSuccess: onDone });
  const mineAsCover = h.cover.id === me;
  const statusText = h.status === 'ACTIVE' ? (h.coveringNow ? 'Covering now' : 'Active · waiting for quiet hours') : HANDOVER_STATUS_LABEL[h.status];
  return (
    <Card tone={h.status === 'REQUESTED' && mineAsCover ? 'warn' : h.coveringNow ? 'live' : undefined} className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-base font-semibold">
            {h.owner.name} <span className="text-muted">→</span> {h.cover.name}
          </span>
          <span className="text-sm text-sub">{periodText(h, tz)}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Pill tone={STATUS_TONE[h.status]}>{statusText}</Pill>
          <Pill tone={h.rights === 'MANAGE' ? 'accent' : 'neutral'}>{HANDOVER_RIGHTS_LABEL[h.rights]}</Pill>
        </div>
      </div>
      <ul aria-label="Handed over" className="m-0 flex list-none flex-wrap gap-2 p-0">
        {h.items.map((i) => (
          <li key={i.id}>
            <Link to={i.kind === 'trade' ? `/trades/${i.id}` : `/setups/${i.id}`} className="inline-flex items-center gap-1.5 rounded-lg bg-raised px-2.5 py-1.5 text-[13px] text-ink no-underline">
              <span className="text-xs text-muted">{i.kind === 'trade' ? 'Trade' : 'Setup'}</span>
              <span className="font-semibold">{i.symbol}</span>
              <span className={dirClass(i.direction)}>{i.direction === 'LONG' ? '▲' : '▼'}</span>
            </Link>
          </li>
        ))}
      </ul>
      {h.notes && <p className="m-0 rounded-lg border-l-2 border-line2 bg-raised px-3 py-2 leading-relaxed whitespace-pre-line text-ink2">{h.notes}</p>}
      {h.summary.text && <p className="m-0 text-sm text-sub">{h.summary.text}</p>}
      <ErrorText error={act.error ? errorText(act.error) : null} />
      {(h.can.answer || h.can.end) && (
        <div className="flex flex-wrap items-center gap-2">
          {h.can.answer && (
            <>
              <Button variant="success" onClick={() => act.mutate('accept')} disabled={act.isPending}>
                Accept
              </Button>
              <Button variant="danger" onClick={() => act.mutate('decline')} disabled={act.isPending}>
                Decline
              </Button>
              {h.acceptBy && <span className="text-xs text-warn">Answer {fmtCountdown(h.acceptBy, now).replace('overdue', 'was due')}</span>}
            </>
          )}
          {h.can.end && (
            <Button variant="ghost" onClick={() => act.mutate('end')} disabled={act.isPending}>
              {h.status === 'ACTIVE' ? 'Take back now' : 'Withdraw'}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

export function Handovers() {
  const me = useMe();
  const qc = useQueryClient();
  const now = useNow(15_000);
  const tz = me.data?.timezone ?? 'UTC';
  const { data, isLoading } = useQuery({ queryKey: ['handovers'], queryFn: () => api<HandoverView[]>('/handovers'), refetchInterval: 30_000 });
  const refresh = () => ['handovers', 'today', 'trade', 'setup', 'setups'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
  const uid = me.data?.id ?? '';
  const live = (h: HandoverView) => h.status === 'REQUESTED' || h.status === 'ACCEPTED' || h.status === 'ACTIVE';
  const groups = [
    { title: 'Waiting for your answer', items: data?.filter((h) => h.status === 'REQUESTED' && h.cover.id === uid) ?? [] },
    { title: "You're covering", items: data?.filter((h) => live(h) && h.status !== 'REQUESTED' && h.cover.id === uid) ?? [] },
    { title: 'Handed over by you', items: data?.filter((h) => live(h) && h.owner.id === uid) ?? [] },
  ];
  const history = data?.filter((h) => !live(h)).slice(0, 20) ?? [];

  return (
    <>
      <PageHeader
        title="Handovers"
        sub="Cover each other's trades and setups during quiet hours or time away. VTF never places orders: covers report, and record broker actions only with manage rights."
        right={
          <Link to="/handovers/new" className="rounded-lg bg-accent px-4 py-2.5 font-bold text-bg no-underline hover:text-bg">
            + Hand over
          </Link>
        }
      />
      {isLoading && <Card className="p-4 text-sub">Loading…</Card>}
      {groups.map(
        (g) =>
          !!g.items.length && (
            <section key={g.title} aria-label={g.title} className="flex flex-col gap-2.5">
              <h2 className="m-0 text-[15px] font-semibold">{g.title}</h2>
              <div className="grid gap-3 lg:grid-cols-2">
                {g.items.map((h) => (
                  <HandoverCard key={h.id} h={h} me={uid} tz={tz} now={now} onDone={refresh} />
                ))}
              </div>
            </section>
          ),
      )}
      {!isLoading && groups.every((g) => !g.items.length) && (
        <Card>
          <Empty>No live handovers. Going offline or into quiet hours with open trades? Hand them over so reporting continues.</Empty>
        </Card>
      )}
      {!!history.length && (
        <Card>
          <h2 className="m-0 border-b border-line px-4 py-3 text-[15px] font-semibold">History</h2>
          {history.map((h) => (
            <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0">
              <span>
                <strong>{h.owner.name}</strong> <span className="text-muted">→</span> <strong>{h.cover.name}</strong> <span className="text-sub">· {h.label} · {fmtTime(h.requestedAt, tz, true)}</span>
              </span>
              <span className="flex items-center gap-2 text-xs text-sub">
                {h.summary.text && <span className="hidden md:inline">{h.summary.text}</span>}
                <Pill tone={STATUS_TONE[h.status]}>{HANDOVER_STATUS_LABEL[h.status]}</Pill>
              </span>
            </div>
          ))}
        </Card>
      )}
    </>
  );
}

const hours = (n: number) => new Date(Date.now() + n * 3600_000);

/** INT-PRG-05: owner picks trades/setups, a cover (same desk first), when, rights and notes. */
export function HandoverNew() {
  const me = useMe();
  // default times are typed in the trader's own timezone, so wait until it is known
  return me.data ? <HandoverForm tz={me.data.timezone} /> : null;
}

function HandoverForm({ tz }: { tz: string }) {
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const { data: trades } = useQuery({ queryKey: ['trades', 'OPEN', 'mine'], queryFn: () => api<TradeView[]>('/trades?status=OPEN&scope=mine') });
  const { data: setups } = useQuery({ queryKey: ['setups', 'mine', 'active'], queryFn: () => api<SetupView[]>('/setups?scope=mine&status=active') });
  const myTrades = useMemo(() => trades?.filter((t) => t.owner.id === me.data?.id) ?? [], [trades, me.data]);
  const [picked, setPicked] = useState<Set<string>>(() => new Set([params.get('trade'), params.get('setup')].filter(Boolean) as string[]));
  // same-desk = trades the markets of what's picked, so the suggestions follow the selection
  const pickedTrades = myTrades.filter((t) => picked.has(t.id)).map((t) => t.id);
  const pickedSetups = (setups ?? []).filter((s) => picked.has(s.id)).map((s) => s.id);
  const { data: candidates } = useQuery({
    queryKey: ['handover-candidates', pickedTrades.join(','), pickedSetups.join(',')],
    queryFn: () => api<HandoverCandidate[]>(`/handovers/candidates?${new URLSearchParams({ tradeIds: pickedTrades.join(','), setupIds: pickedSetups.join(',') })}`),
    placeholderData: (prev) => prev,
  });
  const [coverId, setCoverId] = useState('');
  const [mode, setMode] = useState<'WINDOW' | 'QUIET_HOURS'>('WINDOW');
  const [from, setFrom] = useState(() => utcToLocalInput(new Date(), tz));
  const [until, setUntil] = useState(() => utcToLocalInput(hours(8), tz));
  const [untilRecurring, setUntilRecurring] = useState(() => utcToLocalInput(hours(24 * 7), tz).slice(0, 10) + 'T09:00');
  const [rights, setRights] = useState<'REPORT_ONLY' | 'MANAGE'>('REPORT_ONLY');
  const [notes, setNotes] = useState('');
  const cover = candidates?.find((c) => c.id === coverId) ?? candidates?.[0];
  const quiet = me.data?.quietHours;
  const hasQuiet = !!(quiet?.enabled && quiet.start && quiet.end);

  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const tradeIds = pickedTrades;
  const setupIds = pickedSetups;

  const send = useMutation({
    mutationFn: () =>
      api<HandoverView>('/handovers', {
        body: {
          coverId: cover!.id,
          tradeIds,
          setupIds,
          mode,
          rights,
          notes: notes || null,
          ...(mode === 'WINDOW' ? { startsAt: localInputToUtc(from, tz)?.toISOString(), endsAt: localInputToUtc(until, tz)?.toISOString() } : { endsAt: localInputToUtc(untilRecurring, tz)?.toISOString() }),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['handovers'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
      nav('/handovers');
    },
  });
  const ready = !!cover && tradeIds.length + setupIds.length > 0 && (mode === 'WINDOW' ? !!from && !!until : hasQuiet && !!untilRecurring);
  const same = candidates?.filter((c) => c.sameDesk) ?? [];
  const other = candidates?.filter((c) => !c.sameDesk) ?? [];
  const label = (c: HandoverCandidate) => `${c.name}${c.markets.length ? ` · ${c.markets.join(', ')}` : ''}${c.inQuietHoursNow ? ' · in quiet hours now' : ''}`;

  return (
    <>
      <PageHeader
        back={<Link to="/handovers" className="text-[13px]">← Handovers</Link>}
        title="Hand over trades"
        sub="Reporting continues while you sleep or are away. The cover has to accept first."
        right={
          <Button variant="primary" onClick={() => send.mutate()} disabled={!ready || send.isPending}>
            {send.isPending ? 'Sending…' : 'Send request'}
          </Button>
        }
      />
      <ErrorText error={send.error ? errorText(send.error) : null} />
      <div className="flex flex-wrap items-start gap-4">
        <section className="flex min-w-0 flex-[999_1_460px] flex-col gap-3.5">
          <Card className="flex flex-col gap-2 p-4">
            <h2 className="m-0 text-[15px] font-semibold">What to hand over</h2>
            {!myTrades.length && !setups?.length && <span className="text-sub">You have no open trades or active setups.</span>}
            {myTrades.map((t) => (
              <label key={t.id} className={cx('flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5', picked.has(t.id) ? 'border-[#2b4a78] bg-accent-bg' : 'border-line bg-raised')}>
                <input type="checkbox" className="h-[18px] w-[18px]" checked={picked.has(t.id)} onChange={() => toggle(t.id)} aria-label={`Trade ${t.symbol} ${t.direction === 'LONG' ? 'long' : 'short'} on ${t.account.code}`} />
                <span className="flex flex-1 flex-wrap items-baseline justify-between gap-2">
                  <span>
                    <span className="text-xs text-muted">Trade </span>
                    <strong>{t.symbol}</strong> <span className={dirClass(t.direction)}>{dirLabel(t.direction)}</span> <span className="text-sub">· {t.account.code}</span>
                  </span>
                  <span className="text-xs text-muted">{t.nextReport ? `report #${t.nextReport.sequence} due ${fmtTime(t.nextReport.dueAt, tz, true)}` : ''}</span>
                </span>
              </label>
            ))}
            {setups?.map((s) => (
              <label key={s.id} className={cx('flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5', picked.has(s.id) ? 'border-[#2b4a78] bg-accent-bg' : 'border-line bg-raised')}>
                <input type="checkbox" className="h-[18px] w-[18px]" checked={picked.has(s.id)} onChange={() => toggle(s.id)} aria-label={`Setup ${s.symbol} ${s.direction === 'LONG' ? 'long' : 'short'}`} />
                <span className="flex flex-1 flex-wrap items-baseline justify-between gap-2">
                  <span>
                    <span className="text-xs text-muted">Setup </span>
                    <strong>{s.symbol}</strong> <span className={dirClass(s.direction)}>{dirLabel(s.direction)}</span> <span className="text-sub">· {s.timeframe}</span>
                  </span>
                  <span className="text-xs text-muted">{s.nextUpdate ? `update due ${fmtTime(s.nextUpdate.dueAt, tz, true)}` : s.stale ? 'stale' : ''}</span>
                </span>
              </label>
            ))}
          </Card>
          <Card className="flex flex-col gap-3 p-4">
            <h2 className="m-0 text-[15px] font-semibold">When</h2>
            <Segmented
              label="When"
              options={[
                { value: 'WINDOW', label: 'For a set time' },
                { value: 'QUIET_HOURS', label: 'During my quiet hours, every night' },
              ]}
              value={mode}
              onChange={setMode}
            />
            {mode === 'WINDOW' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={`From (${tz})`}>
                  <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
                </Field>
                <Field label={`Until (${tz})`} hint="Everything comes back to you automatically, with a summary.">
                  <Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
                </Field>
              </div>
            ) : hasQuiet ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1 rounded-lg bg-raised p-3">
                  <span className="text-xs text-muted">Your quiet hours</span>
                  <span className="font-mono font-semibold">
                    {quiet!.start}–{quiet!.end}
                  </span>
                  <span className="text-xs text-muted">{tz} · change them in Settings</span>
                </div>
                <Field label={`Every night until (${tz})`} hint="Each morning the trades come back to you with an overnight summary.">
                  <Input type="datetime-local" value={untilRecurring} onChange={(e) => setUntilRecurring(e.target.value)} />
                </Field>
              </div>
            ) : (
              <p className="m-0 rounded-lg border border-[#5a4316] bg-warn-bg px-3 py-2 text-sm text-warn">
                You haven't set quiet hours yet. <Link to="/settings">Set them in Settings</Link>, then come back.
              </p>
            )}
          </Card>
        </section>
        <aside aria-label="Cover and rights" className="flex min-w-0 flex-[1_1_340px] flex-col gap-3.5">
          <Card className="flex flex-col gap-3 p-4">
            <h2 className="m-0 text-[15px] font-semibold">Who covers</h2>
            <Field label="Covering trader" hint={cover ? (cover.quietHours ? `${cover.name.split(' ')[0]}'s quiet hours: ${cover.quietHours.start}–${cover.quietHours.end} ${cover.quietHours.tz}` : `${cover.name.split(' ')[0]} has no quiet hours set.`) : undefined}>
              <Select value={cover?.id ?? ''} onChange={(e) => setCoverId(e.target.value)}>
                {!!same.length && (
                  <optgroup label="Same desk">
                    {same.map((c) => (
                      <option key={c.id} value={c.id}>
                        {label(c)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {!!other.length && (
                  <optgroup label="Other traders">
                    {other.map((c) => (
                      <option key={c.id} value={c.id}>
                        {label(c)}
                      </option>
                    ))}
                  </optgroup>
                )}
              </Select>
            </Field>
          </Card>
          <Card className="flex flex-col gap-3 p-4">
            <h2 className="m-0 text-[15px] font-semibold">Rights</h2>
            <Segmented
              label="Rights"
              options={[
                { value: 'REPORT_ONLY', label: 'Report only' },
                { value: 'MANAGE', label: 'Manage' },
              ]}
              value={rights}
              onChange={setRights}
            />
            <span className="text-sm leading-relaxed text-sub">
              {rights === 'REPORT_ONLY'
                ? 'The cover posts progress reports and setup updates for you. Stop and target moves, partials and closes stay with you.'
                : 'The cover can also record stop and target moves, partials and a close that were done at the broker. VTF never places orders.'}
            </span>
            <Field label="Handover notes">
              <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Plan, levels to watch, what to do if the stop or target gets close" />
            </Field>
          </Card>
        </aside>
      </div>
    </>
  );
}
